import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import useResellerTransactions from "../../hooks/useResellerTransactions";
import useResellerPlans from "../../hooks/useResellerPlans";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  dialog: {
    padding: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxHeight: 300,
    overflowY: "auto",
  },
  formControl: {
    minWidth: 200,
    width: "100%",
  },
  rechargeAction: {
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));

const ResellerTransactionModal = ({ open, onClose, companyId }) => {
  const classes = useStyles();
  const { getTransactions, rechargeBalance } = useResellerTransactions();
  const { list: listPlans } = useResellerPlans();

  const [transactions, setTransactions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && companyId) {
      loadData();
    }
  }, [open, companyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [txs, pList] = await Promise.all([
        getTransactions(companyId),
        listPlans(),
      ]);
      setTransactions(txs);
      setPlans(pList);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async () => {
    if (!selectedPlanId) {
      toast.error(i18n.t("resellerTransactions.selectPlanError") || "Seleccione un plan");
      return;
    }
    setLoading(true);
    try {
      await rechargeBalance(companyId, selectedPlanId);
      toast.success(i18n.t("resellerTransactions.rechargeSuccess") || "Recarga exitosa");
      loadData(); // reload transactions
      setSelectedPlanId("");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{i18n.t("resellerTransactions.title") || "Historial y Recargas"}</DialogTitle>
      <DialogContent className={classes.dialog}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="reseller-plan-select-label">
                {i18n.t("resellerTransactions.selectPlan") || "Seleccione Plan Reseller"}
              </InputLabel>
              <Select
                labelId="reseller-plan-select-label"
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                label={i18n.t("resellerTransactions.selectPlan") || "Seleccione Plan Reseller"}
              >
                {plans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} - ${p.value} ({p.discountPercentage}%)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <ButtonWithSpinner
              loading={loading}
              variant="contained"
              color="primary"
              onClick={handleRecharge}
              disabled={!selectedPlanId}
              fullWidth
            >
              {i18n.t("resellerTransactions.rechargeButton") || "RECHARGE"}
            </ButtonWithSpinner>
          </Grid>
        </Grid>

        <Paper className={classes.tableContainer}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("resellerTransactions.date") || "Fecha"}</TableCell>
                <TableCell>{i18n.t("resellerTransactions.type") || "Tipo"}</TableCell>
                <TableCell>{i18n.t("resellerTransactions.description") || "Descripción"}</TableCell>
                <TableCell align="right">{i18n.t("resellerTransactions.amount") || "Monto"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length > 0 ? (
                transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>{moment(tx.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell>{tx.type ? tx.type.toUpperCase() : ""}</TableCell>
                    <TableCell>{tx.description}</TableCell>
                    <TableCell align="right">
                      ${tx.amount.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {i18n.t("resellerTransactions.noRecords") || "No hay recargas registradas."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined" disabled={loading}>
          {i18n.t("resellerTransactions.close") || "Cerrar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResellerTransactionModal;
